<template>
  <div>
    <a-modal
      :visible="visible"
      :title="title"
      :width="600"
      :footer="showActions ? undefined : null"
      @ok="handleConfirm"
      @cancel="handleCancel"
    >
      <div class="upload-container">
        <div class="media-content">
          <div class="media-grid">
            <div v-for="(item, index) in mediaList" :key="item.key || index" class="media-item">
              <!-- 图片显示 -->
              <div v-if="item.type === 'image'" class="image-wrapper">
                <mpm-image :url="formatImageUrl(item.url)" width="80" height="80" alt="上传图片" />
              </div>
              <!-- 视频显示 -->
              <div v-else-if="item.type === 'video'" class="video-wrapper">
                <video
                  :src="formatImageUrl(item.videoSrc)"
                  :poster="item.cover ? formatImageUrl(item.cover) : undefined"
                  width="80"
                  height="80"
                  muted
                  @click="previewVideo(item.videoSrc)"
                >
                  您的浏览器不支持视频播放
                </video>
                <div class="video-play-icon">
                  <a-icon type="play-circle" />
                </div>
              </div>

              <div class="media-actions" v-if="showActions">
                <a-icon type="delete" @click="removeFile(item.type, item.originIndex)" />
              </div>
            </div>

            <div class="upload-btn-wrapper" v-if="showActions && totalCount < maxCount">
              <a-upload
                accept="image/*,video/*"
                :show-upload-list="false"
                :before-upload="beforeUpload"
                class="custom-uploader"
              >
                <div class="upload-button">
                  <a-icon :type="loading ? 'loading' : 'plus'" />
                  <div class="upload-text">{{ loading ? '上传中' : '上传文件' }}</div>
                </div>
              </a-upload>
            </div>
          </div>
        </div>
      </div>

      <!-- 视频预览模态框 -->
      <a-modal :visible="videoPreviewVisible" :footer="null" @cancel="closeVideoPreview" :width="720" centered>
        <div class="video-preview-modal">
          <video :src="previewVideoUrl" controls autoplay style="width: 100%; max-height: 60vh">
            您的浏览器不支持视频播放
          </video>
        </div>
      </a-modal>
    </a-modal>
  </div>
</template>

<script>
  import { uploadImage, putObject } from '@/config/alioss'
  import Compressor from 'compressorjs' // 引入图片压缩库
  import { UPLOAD_CDN_HOST } from '@/config/env'

  export default {
    name: 'AddImage',
    props: {
      visible: {
        type: Boolean,
        default: false,
      },
      title: {
        type: String,
        default: '媒体附件',
      },
      maxCount: {
        type: Number,
        default: 9,
      },
      initialImages: {
        type: Array,
        default: () => [],
      },
      initialVideos: {
        type: Array,
        default: () => [],
      },
      showActions: {
        type: Boolean,
        default: true,
      },
    },
    data() {
      return {
        uploadedImages: [],
        uploadedVideos: [],
        loading: false,
        baseUrl: UPLOAD_CDN_HOST[process.env.VUE_APP_SERVER_ENV],
        videoPreviewVisible: false,
        previewVideoUrl: '',
      }
    },
    watch: {
      visible(val) {
        if (val) {
          this.initFileList(this.initialImages, this.initialVideos)
        }
      },
    },
    computed: {
      mediaList() {
        const images = (this.uploadedImages || []).map((url, index) => ({
          type: 'image',
          url,
          originIndex: index,
          key: `image-${index}-${url}`,
        }))
        const videos = (this.uploadedVideos || [])
          .map((video, index) => {
            const normalized = this.normalizeVideoItem(video)
            if (!normalized || !normalized.imageUrl) return null
            return {
              type: 'video',
              videoSrc: normalized.imageUrl,
              cover: normalized.videoCoverUrl || '',
              originIndex: index,
              key: `video-${index}-${normalized.id || normalized.imageUrl || index}`,
            }
          })
          .filter(Boolean)
        return [...images, ...videos]
      },
      totalCount() {
        return this.uploadedImages.length + this.uploadedVideos.length
      },
    },
    methods: {
      initFileList(images = [], videos = []) {
        this.uploadedImages = [...images]
        this.uploadedVideos = videos.filter((item) => item && item.imageUrl)
      },
      normalizeVideoItem(item) {
        if (!item) return null
        return {
          id: item.id,
          imageUrl: item.imageUrl,
          videoCoverUrl: item.videoCoverUrl || '',
        }
      },
      handleCancel() {
        this.$emit('cancel')
      },
      handleConfirm() {
        this.$emit('confirm', this.uploadedImages, this.uploadedVideos)
      },
      removeFile(type, index) {
        if (type === 'video') {
          this.uploadedVideos.splice(index, 1)
        } else {
          this.uploadedImages.splice(index, 1)
        }
      },

      previewVideo(url) {
        this.previewVideoUrl = this.formatImageUrl(url)
        this.videoPreviewVisible = true
      },
      closeVideoPreview() {
        this.videoPreviewVisible = false
        this.previewVideoUrl = ''
      },
      formatImageUrl(url) {
        if (!url) return ''
        // 检查是否是完整的URL
        if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
          return url
        }

        // 使用环境变量中的CDN地址
        return this.baseUrl + url
      },
      beforeUpload(file) {
        if (this.totalCount >= this.maxCount) {
          this.$message.warning(`最多只能上传${this.maxCount}个文件`)
          return false
        }

        if (file.type.startsWith('image/')) {
          return this.beforeUploadImage(file)
        } else if (file.type.startsWith('video/')) {
          return this.beforeUploadVideo(file)
        } else {
          this.$message.error('不支持的文件类型，请上传图片或视频文件!')
          return false
        }
      },
      beforeUploadImage(file) {
        if (file.size / 1024 / 1024 > 10) {
          this.$message.warning('图片正在压缩中...')
          this.compressImage(file)
          return false
        }
        this.handleUpload(file, 'image')
        return false
      },
      beforeUploadVideo(file) {
        if (file.size / 1024 / 1024 > 100) {
          this.$message.error('视频文件不能超过100MB!')
          return false
        }
        return this.checkVideoDuration(file)
      },

      checkVideoDuration(file) {
        return new Promise((resolve) => {
          const video = document.createElement('video')
          video.preload = 'metadata'
          video.onloadedmetadata = () => {
            window.URL.revokeObjectURL(video.src)
            if (video.duration > 300) {
              this.$message.error('视频时长不能超过5分钟')
              resolve(false)
            } else {
              this.handleUpload(file, 'video')
              resolve(false)
            }
          }
          video.onerror = () => {
            this.$message.error('无法读取视频文件')
            resolve(false)
          }
          video.src = URL.createObjectURL(file)
        })
      },
      compressImage(file) {
        new Compressor(file, {
          quality: 0.8,
          success: (result) => this.handleUpload(result, 'image'),
          error: (err) => {
            console.error('图片压缩失败:', err)
            this.$message.error('图片压缩失败')
          },
        })
      },

      async handleUpload(file, type) {
        this.loading = true
        try {
          if (type === 'image') {
            const result = await uploadImage(file)
            if (result?.name) {
              this.uploadedImages.push(result.name)
              this.$message.success('图片上传成功')
            } else {
              this.$message.error('上传失败')
            }
            return
          }

          // 视频上传：生成封面图并上传
          const coverImage = await this.getVideoFirstFrame(file)
          const result = await putObject(file)

          if (!result?.name) {
            this.$message.error('上传失败')
            return
          }

          let videoCoverUrl = ''
          if (coverImage) {
            try {
              const coverResult = await putObject(coverImage)
              videoCoverUrl = coverResult?.name || ''
            } catch (error) {
              console.error('封面图上传失败:', error)
            }
          }

          this.uploadedVideos.push({
            imageUrl: result.name,
            videoCoverUrl,
          })
          this.$message.success('视频上传成功')
        } catch (error) {
          console.error('上传失败:', error)
          this.$message.error('上传失败: ' + (error.message || '未知错误'))
        } finally {
          this.loading = false
        }
      },

      getVideoFirstFrame(videoFile) {
        return new Promise((resolve) => {
          const video = document.createElement('video')
          video.preload = 'auto'
          video.muted = true
          video.playsInline = true
          video.crossOrigin = 'anonymous'

          video.addEventListener('loadedmetadata', () => {
            video.currentTime = 1.0
          })

          video.addEventListener('seeked', () => {
            const canvas = document.createElement('canvas')
            canvas.width = video.videoWidth
            canvas.height = video.videoHeight
            const ctx = canvas.getContext('2d')
            ctx.drawImage(video, 0, 0)

            canvas.toBlob(
              (blob) => {
                if (blob) {
                  resolve(
                    new File([blob], `${videoFile.name.split('.')[0]}_cover.jpg`, {
                      type: 'image/jpeg',
                      lastModified: Date.now(),
                    })
                  )
                } else {
                  resolve(null)
                }
                window.URL.revokeObjectURL(video.src)
              },
              'image/jpeg',
              0.9
            )
          })

          video.onerror = () => {
            console.error('获取视频首帧失败')
            window.URL.revokeObjectURL(video.src)
            resolve(null)
          }

          video.src = URL.createObjectURL(videoFile)
        })
      },
    },
  }
</script>

<style lang="scss" scoped>
  .upload-container {
    padding: 10px;

    .media-content {
      margin-bottom: 20px;
    }

    .media-grid {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
    }

    .media-item {
      position: relative;
      width: 80px;
      height: 80px;
      border: 1px solid #e8e8e8;
      border-radius: 4px;
      overflow: hidden;

      .image-wrapper,
      .video-wrapper {
        width: 100%;
        height: 100%;
        position: relative;
      }

      .video-wrapper {
        cursor: pointer;

        video {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .video-play-icon {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: rgba(255, 255, 255, 0.8);
          font-size: 24px;
          pointer-events: none;
        }

        &:hover {
          .video-play-icon {
            color: #1890ff;
          }
        }
      }

      .media-actions {
        position: absolute;
        top: 5px;
        right: 5px;
        background-color: rgba(0, 0, 0, 0.5);
        border-radius: 50%;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        opacity: 0;
        transition: opacity 0.3s;

        i {
          color: #fff;
          font-size: 14px;
        }
      }

      &:hover {
        .media-actions {
          opacity: 1;
        }
      }
    }

    .upload-btn-wrapper {
      width: 80px;
      height: 80px;
    }

    .custom-uploader {
      width: 100%;
      height: 100%;
    }

    .upload-button {
      width: 80px;
      height: 80px;
      border: 1px dashed #d9d9d9;
      border-radius: 4px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      transition: border-color 0.3s;

      &:hover {
        border-color: #1890ff;
      }

      .anticon {
        font-size: 24px;
        color: #999;
      }

      .upload-text {
        margin-top: 8px;
        font-size: 12px;
        color: #666;
        text-align: center;
      }
    }
  }

  .video-preview-modal {
    text-align: center;

    video {
      border-radius: 4px;
    }
  }
</style>
